import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * GamificationProfile - Componente que exibe nível, XP, badges e ranking do usuário
 * Usado em perfis de usuário e dashboard
 * 
 * Props:
 * - userId: ID do usuário para exibir stats
 * - compact: versão compacta do card
 * - showLeaderboard: exibir ranking (apenas admin vê o ranking completo)
 * - creatorId: se estiver na página de um criador, filtrar para mostrar apenas ele
 */
const GamificationProfile = ({ userId, compact = false, showLeaderboard = false, creatorId = null }) => {
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingDaily, setClaimingDaily] = useState(false);
  const { user: currentUser } = useAuth();
  
  // Verificar se é admin ou membro fundador
  const isAdmin = currentUser?.role === 'admin';
  const isFundador = currentUser?.is_fundador;

  const loadStats = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/gamification/stats?user_id=${userId}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading gamification stats:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadLeaderboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/gamification/leaderboard?limit=10`);
      let data = response.data.leaderboard || [];
      
      // Se não é admin, filtrar o leaderboard
      if (!isAdmin && data.length > 0) {
        // Mostrar apenas:
        // 1. O criador específico (se estiver na página de um criador)
        // 2. O usuário logado (se for membro fundador)
        // 3. O próprio usuário do perfil (userId)
        const allowedIds = new Set();
        
        // Adicionar o criador da página (se estiver em página de criador)
        if (creatorId) {
          allowedIds.add(creatorId);
        }
        
        // Adicionar o usuário do perfil
        if (userId) {
          allowedIds.add(userId);
        }
        
        // Adicionar o usuário logado se for membro fundador
        if (isFundador && currentUser?.id) {
          allowedIds.add(currentUser.id);
        }
        
        data = data.filter(entry => allowedIds.has(entry.user_id));
      }
      
      setLeaderboard(data);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  }, [isAdmin, isFundador, currentUser, creatorId, userId]);

  useEffect(() => {
    loadStats();
    if (showLeaderboard) {
      loadLeaderboard();
    }
  }, [loadStats, loadLeaderboard, showLeaderboard]);

  const claimDailyLogin = async () => {
    if (!userId) return;
    
    try {
      setClaimingDaily(true);
      const response = await axios.post(`${API}/api/gamification/daily-login?user_id=${userId}`);
      
      if (response.data.already_claimed) {
        toast.info('XP diário já reivindicado hoje! 📅');
      } else {
        toast.success(`+${response.data.xp_gained} XP ganhos! 🎉`);
        
        if (response.data.level_up) {
          toast.success(`🎊 Parabéns! Você subiu para o nível ${response.data.level}!`);
        }
        
        if (response.data.new_badges?.length > 0) {
          response.data.new_badges.forEach(badge => {
            toast.success(`🏅 Novo badge: ${badge.icon} ${badge.name}!`);
          });
        }
        
        // Recarregar stats
        await loadStats();
      }
    } catch (error) {
      console.error('Error claiming daily login:', error);
      toast.error('Erro ao reivindicar XP diário');
    } finally {
      setClaimingDaily(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-[#1A1A1A] rounded-xl p-4">
        <div className="h-4 bg-white/10 rounded w-1/2 mb-2"></div>
        <div className="h-2 bg-white/10 rounded w-full mb-4"></div>
        <div className="flex gap-2">
          <div className="h-8 w-8 bg-white/10 rounded-full"></div>
          <div className="h-8 w-8 bg-white/10 rounded-full"></div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  // Versão compacta para cards pequenos
  if (compact) {
    return (
      <div className="bg-gradient-to-r from-[#1A1A1A] to-[#0D0D0D] border border-[#C79F5E]/20 rounded-xl p-4">
        {/* Nível e XP */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C79F5E] to-[#8B6914] flex items-center justify-center text-white font-bold text-lg">
                {stats.level}
              </div>
              <div className="absolute -bottom-1 -right-1 bg-[#D4AF37] text-black text-xs px-1.5 py-0.5 rounded-full font-bold">
                LV
              </div>
            </div>
            <div>
              <p className="text-white font-bold">Nível {stats.level}</p>
              <p className="text-xs text-gray-400">{stats.total_xp.toLocaleString()} XP total</p>
            </div>
          </div>
          {stats.rank && (
            <div className="text-right">
              <p className="text-[#C79F5E] font-bold">#{stats.rank}</p>
              <p className="text-xs text-gray-400">Ranking</p>
            </div>
          )}
        </div>

        {/* Barra de XP */}
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>Progresso</span>
            <span>{Math.round(stats.xp_progress_percent)}%</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#C79F5E] to-[#D4AF37] rounded-full transition-all duration-500"
              style={{ width: `${stats.xp_progress_percent}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {stats.xp_to_next_level.toLocaleString()} XP para o próximo nível
          </p>
        </div>

        {/* Badges */}
        {stats.badges?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {stats.badges.slice(0, 5).map((badge, idx) => (
              <div 
                key={idx}
                className="bg-white/5 px-2 py-1 rounded-full text-sm"
                title={`${badge.name}: ${badge.description}`}
              >
                {badge.icon}
              </div>
            ))}
            {stats.badges.length > 5 && (
              <div className="bg-white/5 px-2 py-1 rounded-full text-xs text-gray-400">
                +{stats.badges.length - 5}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Versão completa
  return (
    <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border border-[#C79F5E]/20 rounded-2xl overflow-hidden">
      {/* Header com gradiente */}
      <div className="bg-gradient-to-r from-[#4D1A4A]/30 to-[#C79F5E]/20 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Círculo de nível */}
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#C79F5E] via-[#D4AF37] to-[#8B6914] p-1">
                <div className="w-full h-full rounded-full bg-[#1A1A1A] flex items-center justify-center">
                  <span className="text-3xl font-black text-[#C79F5E]">{stats.level}</span>
                </div>
              </div>
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-[#C79F5E] to-[#D4AF37] text-black text-xs px-3 py-1 rounded-full font-bold">
                NÍVEL
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-white">Nível {stats.level}</h3>
              <p className="text-[#C79F5E] font-semibold">{stats.total_xp.toLocaleString()} XP Total</p>
              {stats.daily_login_streak > 0 && (
                <p className="text-sm text-orange-400 flex items-center gap-1">
                  🔥 {stats.daily_login_streak} dias consecutivos
                </p>
              )}
            </div>
          </div>

          {/* Ranking */}
          {stats.rank && (
            <div className="text-center bg-white/5 px-6 py-3 rounded-xl">
              <p className="text-3xl font-black text-[#D4AF37]">#{stats.rank}</p>
              <p className="text-xs text-gray-400">no Ranking</p>
            </div>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      <div className="p-6">
        {/* Barra de progresso de XP */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-400">Progresso para Nível {stats.level + 1}</span>
            <span className="text-sm font-bold text-[#C79F5E]">{Math.round(stats.xp_progress_percent)}%</span>
          </div>
          <div className="h-4 bg-white/10 rounded-full overflow-hidden relative">
            <div 
              className="h-full bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${stats.xp_progress_percent}%` }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-white drop-shadow-lg">
                {stats.xp.toLocaleString()} / {(stats.xp + stats.xp_to_next_level).toLocaleString()} XP
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1 text-right">
            Faltam {stats.xp_to_next_level.toLocaleString()} XP
          </p>
        </div>

        {/* Botão de Daily Login */}
        <button
          onClick={claimDailyLogin}
          disabled={claimingDaily}
          className="w-full mb-6 py-3 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-500 hover:to-emerald-400 text-white font-bold rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {claimingDaily ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Reivindicando...
            </>
          ) : (
            <>
              🎁 Reivindicar XP Diário
            </>
          )}
        </button>

        {/* Badges */}
        <div className="mb-6">
          <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            🏅 Badges Conquistados
            <span className="text-sm font-normal text-gray-400">({stats.badges?.length || 0})</span>
          </h4>
          
          {stats.badges?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {stats.badges.map((badge, idx) => (
                <div 
                  key={idx}
                  className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-3 transition-all cursor-pointer group"
                >
                  <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">{badge.icon}</div>
                  <p className="text-sm font-semibold text-white">{badge.name}</p>
                  <p className="text-xs text-gray-400 line-clamp-2">{badge.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-white/5 rounded-xl">
              <p className="text-4xl mb-2">🎯</p>
              <p className="text-gray-400">Nenhum badge ainda</p>
              <p className="text-xs text-gray-500">Continue ativo para conquistar badges!</p>
            </div>
          )}
        </div>

        {/* Leaderboard */}
        {showLeaderboard && leaderboard.length > 0 && (
          <div>
            <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              🏆 {isAdmin ? 'Top 10 Ranking' : 'Seu Ranking'}
            </h4>
            <div className="space-y-2">
              {leaderboard.map((entry, idx) => (
                <div 
                  key={entry.user_id}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                    entry.user_id === userId 
                      ? 'bg-[#C79F5E]/20 border border-[#C79F5E]/30' 
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  {/* Posição */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                    idx === 0 ? 'bg-yellow-500 text-black' :
                    idx === 1 ? 'bg-gray-400 text-black' :
                    idx === 2 ? 'bg-amber-700 text-white' :
                    'bg-white/10 text-gray-400'
                  }`}>
                    {entry.rank}
                  </div>

                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4D1A4A] to-[#C79F5E] flex items-center justify-center overflow-hidden">
                    {entry.picture ? (
                      <img src={entry.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white font-bold">{entry.name?.charAt(0) || '?'}</span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">{entry.name}</p>
                    <p className="text-xs text-gray-400">Nível {entry.level}</p>
                  </div>

                  {/* XP */}
                  <div className="text-right">
                    <p className="text-[#C79F5E] font-bold">{entry.total_xp.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">XP</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * GamificationMiniCard - Versão super compacta para navbar ou cards pequenos
 */
export const GamificationMiniCard = ({ userId, onClick }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!userId) return;
    
    const loadStats = async () => {
      try {
        const response = await axios.get(`${API}/api/gamification/stats?user_id=${userId}`);
        setStats(response.data);
      } catch (error) {
        console.error('Error loading mini gamification stats:', error);
      }
    };

    loadStats();
  }, [userId]);

  if (!stats) return null;

  return (
    <button 
      onClick={onClick}
      className="flex items-center gap-2 bg-gradient-to-r from-[#C79F5E]/20 to-transparent px-3 py-1.5 rounded-full hover:from-[#C79F5E]/30 transition-all"
    >
      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#C79F5E] to-[#8B6914] flex items-center justify-center text-white text-xs font-bold">
        {stats.level}
      </div>
      <span className="text-sm text-[#C79F5E] font-semibold">{stats.total_xp.toLocaleString()} XP</span>
    </button>
  );
};

/**
 * BadgeDisplay - Componente para exibir um único badge
 */
export const BadgeDisplay = ({ badge, size = 'md' }) => {
  const sizes = {
    sm: 'text-lg p-1.5',
    md: 'text-2xl p-2',
    lg: 'text-4xl p-3'
  };

  return (
    <div 
      className={`bg-white/5 hover:bg-white/10 rounded-xl ${sizes[size]} inline-flex items-center justify-center transition-all cursor-pointer`}
      title={`${badge.name}: ${badge.description}`}
    >
      {badge.icon}
    </div>
  );
};

export default GamificationProfile;
